import { useEffect } from "react";
import { useFollowsStore } from "@/stores/followsStore";
import { useAuthContext } from "@/context/authContext";

/**
 * Hook for managing user follow relationships (brands and vendors).
 *
 * Automatically loads followed brands/vendors when user is signed in.
 * Resets follow state when user signs out.
 *
 * @returns Object containing:
 * - followedBrands: Set of followed brand IDs
 * - followedVendors: Set of followed vendor IDs
 * - loading: Boolean indicating if data is being fetched
 * - followBrand: Function to follow a brand (optimistic)
 * - unfollowBrand: Function to unfollow a brand (optimistic)
 * - followVendor: Function to follow a vendor (optimistic)
 * - unfollowVendor: Function to unfollow a vendor (optimistic)
 *
 * @example
 * ```tsx
 * function FollowButton({ brandId }) {
 *   const { followedBrands, followBrand, unfollowBrand } = useFollows();
 *   const isFollowing = followedBrands.has(brandId);
 *
 *   return (
 *     <button
 *       onClick={() => isFollowing ? unfollowBrand(brandId) : followBrand(brandId)}
 *     >
 *       {isFollowing ? "Following" : "Follow"}
 *     </button>
 *   );
 * }
 * ```
 */
export const useFollows = () => {
  const { userId, isSignedIn } = useAuthContext();
  const followedBrands = useFollowsStore((state) => state.followedBrands);
  const followedVendors = useFollowsStore((state) => state.followedVendors);
  const loading = useFollowsStore((state) => state.loading);
  const {
    loadFollowedBrands,
    loadFollowedVendors,
    setCurrentUserId,
    reset,
    optimisticallyFollowBrand,
    optimisticallyUnfollowBrand,
    optimisticallyFollowVendor,
    optimisticallyUnfollowVendor,
  } = useFollowsStore((state) => state.actions);

  useEffect(() => {
    if (userId) {
      setCurrentUserId(userId);
    }
  }, [userId, setCurrentUserId]);

  useEffect(() => {
    if (isSignedIn && userId) {
      loadFollowedBrands();
      loadFollowedVendors();
    } else {
      reset();
    }
  }, [isSignedIn, userId, loadFollowedBrands, loadFollowedVendors, reset]);

  return {
    followedBrands,
    followedVendors,
    loading,
    followBrand: optimisticallyFollowBrand,
    unfollowBrand: optimisticallyUnfollowBrand,
    followVendor: optimisticallyFollowVendor,
    unfollowVendor: optimisticallyUnfollowVendor,
  };
};
